import { graphql } from 'app/generated/gql'
import { TagModelType } from 'app/components/tags/TagModelType'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import type { Mutation, Activity, Contact, Workspace } from 'app/generated/graphql'
import { logError } from 'lib/logging'

const ADD_ACTIVITY_TAGS = graphql(`
  mutation AddActivityTags($id: ID!, $tags: [String!]!) {
    addActivityTags(id: $id, tags: $tags) {
      id
      tags
    }
  }
`)

const REMOVE_ACTIVITY_TAGS = graphql(`
  mutation RemoveActivityTags($id: ID!, $tags: [String!]!) {
    removeActivityTags(id: $id, tags: $tags) {
      id
      tags
    }
  }
`)

const ADD_CONTACT_TAGS = graphql(`
  mutation AddContactTags($id: ID!, $tags: [String!]!) {
    addTags(id: $id, tags: $tags) {
      id
      tags
    }
  }
`)

const REMOVE_CONTACT_TAGS = graphql(`
  mutation RemoveContactTags($id: ID!, $tags: [String!]!) {
    removeTags(id: $id, tags: $tags) {
      id
      tags
    }
  }
`)

const GET_ACTIVITY_TAGS = graphql(`
  query ActivityTags($id: ID!) {
    activity(id: $id) {
      id
      tags
    }
  }
`)

const GET_CONTACT_TAGS = graphql(`
  query ContactTags($id: ID!) {
    contact(id: $id) {
      id
      tags
    }
  }
`)

const CREATE_TAG_COLOR = graphql(`
  mutation UpdateTagsCreateTagColor($input: CreateTagColorInput!) {
    createTagColor(input: $input) {
      id
    }
  }
`)

const ADD_WORKSPACE_ORG_TAG = graphql(`
  mutation AddWorkspaceOrgTag($id: ID!, $tag: String!) {
    addWorkspaceOrgTag(id: $id, tag: $tag) {
      id
      orgTags
    }
  }
`)

const REMOVE_WORKSPACE_ORG_TAG = graphql(`
  mutation RemoveWorkspaceOrgTag($id: ID!, $tag: String!) {
    removeWorkspaceOrgTag(id: $id, tag: $tag) {
      id
      orgTags
    }
  }
`)

type TagResult = { updatedTags: boolean; data: { orgTags: string[]; tags: string[] } }

const fail: TagResult = { updatedTags: false, data: { orgTags: [], tags: [] } }

export const useUpdateTags = (
  model: TagModelType,
  modelId?: string,
  isOrgTags?: boolean,
  workspaceId?: string,
): [
  addTag: (tag: string, color?: string) => Promise<TagResult>,
  removeTag: (tag: string) => Promise<TagResult>,
  setTags: (tags: string[]) => Promise<TagResult>,
] => {
  // Activities use addActivityTags/removeActivityTags GraphQL mutations
  if (model === `activities` && !isOrgTags) {
    const onAdd = async (tag: string, color?: string): Promise<TagResult> => {
      if (!modelId) return fail
      try {
        if (color) {
          await gqlMutation(CREATE_TAG_COLOR, {
            input: { entity: `Activity`, name: tag, color },
          })
        }
        const result = await gqlMutation(ADD_ACTIVITY_TAGS, {
          id: modelId,
          tags: [tag],
        })
        if (result.error) return fail
        const saved = result.data?.addActivityTags?.tags ?? [tag]
        return { updatedTags: true, data: { tags: saved, orgTags: [] } }
      } catch (err) {
        logError(`Error adding activity tag`, err)
        return fail
      }
    }

    const onRemove = async (tag: string): Promise<TagResult> => {
      if (!modelId) return fail
      try {
        const result = await gqlMutation(REMOVE_ACTIVITY_TAGS, {
          id: modelId,
          tags: [tag],
        })
        if (result.error) return fail
        const saved = result.data?.removeActivityTags?.tags ?? []
        return { updatedTags: true, data: { tags: saved, orgTags: [] } }
      } catch (err) {
        logError(`Error removing activity tag`, err)
        return fail
      }
    }

    const onSetTags = async (tags: string[]): Promise<TagResult> => {
      if (!modelId) return fail
      try {
        const queryResult = await gqlQuery<{ activity: { tags?: string[] } | undefined }>(GET_ACTIVITY_TAGS, {
          id: modelId,
        })
        const current = queryResult.data?.activity?.tags ?? []

        const toAdd = tags.filter((t) => !current.includes(t))
        const toRemove = current.filter((t) => !tags.includes(t))

        let saved = current
        if (toRemove.length) {
          const removeResult = await gqlMutation(REMOVE_ACTIVITY_TAGS, {
            id: modelId,
            tags: toRemove,
          })
          if (removeResult.error) return fail
          saved = removeResult.data?.removeActivityTags?.tags ?? saved.filter((t) => !toRemove.includes(t))
        }
        if (toAdd.length) {
          const addResult = await gqlMutation(ADD_ACTIVITY_TAGS, {
            id: modelId,
            tags: toAdd,
          })
          if (addResult.error) return fail
          saved = addResult.data?.addActivityTags?.tags ?? [...saved, ...toAdd]
        }

        return { updatedTags: true, data: { tags: saved, orgTags: [] } }
      } catch (err) {
        logError(`Error setting activity tags`, err)
        return fail
      }
    }

    return [onAdd, onRemove, onSetTags]
  }

  // Contacts use addTags/removeTags GraphQL mutations
  if (model === `contacts` && !isOrgTags) {
    const onAdd = async (tag: string, color?: string): Promise<TagResult> => {
      if (!modelId) return fail
      try {
        if (color) {
          await gqlMutation(CREATE_TAG_COLOR, {
            input: { entity: `Contact`, name: tag, color },
          })
        }
        const result = await gqlMutation(ADD_CONTACT_TAGS, {
          id: modelId,
          tags: [tag],
        })
        if (result.error) return fail
        const saved = result.data?.addTags?.tags ?? [tag]
        return { updatedTags: true, data: { tags: saved, orgTags: [] } }
      } catch (err) {
        logError(`Error adding contact tag`, err)
        return fail
      }
    }

    const onRemove = async (tag: string): Promise<TagResult> => {
      if (!modelId) return fail
      try {
        const result = await gqlMutation(REMOVE_CONTACT_TAGS, {
          id: modelId,
          tags: [tag],
        })
        if (result.error) return fail
        const saved = result.data?.removeTags?.tags ?? []
        return { updatedTags: true, data: { tags: saved, orgTags: [] } }
      } catch (err) {
        logError(`Error removing contact tag`, err)
        return fail
      }
    }

    const onSetTags = async (tags: string[]): Promise<TagResult> => {
      if (!modelId) return fail
      try {
        const queryResult = await gqlQuery<{ contact: { tags?: string[] } | undefined }>(GET_CONTACT_TAGS, {
          id: modelId,
        })
        const current = queryResult.data?.contact?.tags ?? []

        const toAdd = tags.filter((t) => !current.includes(t))
        const toRemove = current.filter((t) => !tags.includes(t))

        let saved = current
        if (toRemove.length) {
          const removeResult = await gqlMutation(REMOVE_CONTACT_TAGS, {
            id: modelId,
            tags: toRemove,
          })
          if (removeResult.error) return fail
          saved = removeResult.data?.removeTags?.tags ?? saved.filter((t) => !toRemove.includes(t))
        }
        if (toAdd.length) {
          const addResult = await gqlMutation(ADD_CONTACT_TAGS, {
            id: modelId,
            tags: toAdd,
          })
          if (addResult.error) return fail
          saved = addResult.data?.addTags?.tags ?? [...saved, ...toAdd]
        }

        return { updatedTags: true, data: { tags: saved, orgTags: [] } }
      } catch (err) {
        logError(`Error setting contact tags`, err)
        return fail
      }
    }

    return [onAdd, onRemove, onSetTags]
  }

  // Workspace org tags use addWorkspaceOrgTag/removeWorkspaceOrgTag GraphQL mutations
  if (model === `workspaces` && isOrgTags && modelId) {
    const onAdd = async (tag: string, color?: string): Promise<TagResult> => {
      try {
        if (color) {
          await gqlMutation(CREATE_TAG_COLOR, {
            input: { entity: `Workspace`, name: tag, color },
          })
        }
        const result = await gqlMutation(ADD_WORKSPACE_ORG_TAG, {
          id: modelId,
          tag,
        })
        if (result.error) return fail
        const orgTags = result.data?.addWorkspaceOrgTag?.orgTags ?? []
        return { updatedTags: true, data: { orgTags, tags: [] } }
      } catch (err) {
        logError(`Error adding workspace org tag`, err)
        return fail
      }
    }

    const onRemove = async (tag: string): Promise<TagResult> => {
      try {
        const result = await gqlMutation(REMOVE_WORKSPACE_ORG_TAG, {
          id: modelId,
          tag,
        })
        if (result.error) return fail
        const orgTags = result.data?.removeWorkspaceOrgTag?.orgTags ?? []
        return { updatedTags: true, data: { orgTags, tags: [] } }
      } catch (err) {
        logError(`Error removing workspace org tag`, err)
        return fail
      }
    }

    // TagsCell guards the org tags path with isOrgTags and never calls setTags, so this always returns fail and is never reached at runtime.
    const onSetTags = async (): Promise<TagResult> => fail

    return [onAdd, onRemove, onSetTags]
  }

  // Other models (locations, workspaces) use REST
  const apiEndpoint = isOrgTags ? `/api/${model}/${modelId}/org-tags` : `/api/${model}/${modelId}/tags`

  const onAdd = async (tag: string, color?: string): Promise<TagResult> => {
    try {
      const wId = !isOrgTags ? `workspaceId=${workspaceId}` : ``
      const tagColor = color ? `color=${color}` : ``
      const results = await window.fetch(`${apiEndpoint}?${wId}${wId ? `&${tagColor}` : `${tagColor}`}`, {
        method: `POST`,
        headers: { 'content-type': `application/json` },
        body: JSON.stringify({ tag }),
      })
      if (!results.ok) return fail
      return await results.json()
    } catch (err) {
      logError(`Error adding tag`, err)
      return fail
    }
  }

  const onRemove = async (tag: string): Promise<TagResult> => {
    try {
      const params = workspaceId ? `workspaceId=${workspaceId}&tag=${tag}` : `tag=${tag}`
      const results = await window.fetch(`${apiEndpoint}?${params}`, {
        method: `DELETE`,
        headers: { 'content-type': `application/json` },
      })
      if (!results.ok) return fail
      return await results.json()
    } catch (err) {
      logError(`Error removing tag`, err)
      return fail
    }
  }

  const onSetTags = async (tags: string[]): Promise<TagResult> => {
    try {
      const params = workspaceId ? `workspaceId=${workspaceId}` : ``
      const results = await window.fetch(`${apiEndpoint}?${params}`, {
        method: isOrgTags ? `POST` : `PUT`,
        headers: { 'content-type': `application/json` },
        body: JSON.stringify({ tags }),
      })
      if (!results.ok) return fail
      return await results.json()
    } catch (err) {
      logError(`Error setting tags`, err)
      return fail
    }
  }

  return [onAdd, onRemove, onSetTags]
}
